import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageTitle } from "@/components/DashUI";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const SERVICES = ["Assisted Living", "Family Care Homes", "Memory Care", "Independent Living",
  "Private-Duty Home Care", "Personal Care", "Companion Care", "Residential Respite", "In-Home Respite",
  "Adult Day Services", "Care Management", "Senior Transition / Move Management", "Private Transportation"];

export default function Capacity() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { provider_name: "", service_category: "Assisted Living", provider_type: "", city: "", state: "",
    description: "", quantity: "", price_range: "", available_from: "", active: true };
  const [f, setF] = useState(empty);
  const load = () => api.get("/admin/capacity").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/capacity", f); toast.success("Capacity added"); setOpen(false); setF(empty); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/admin/capacity/${id}`); load(); };

  return (
    <div>
      <PageTitle title="Available Care Capacity" subtitle="Manage the private-pay care capacity marketplace">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-capacity-btn" className="bg-navy hover:bg-navy/90"><Plus className="mr-2 h-4 w-4" /> Add Capacity</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Add available capacity</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="capacity-form">
              <FF label="Provider name" req><Input data-testid="cap-provider" required value={f.provider_name} onChange={set("provider_name")} /></FF>
              <div className="grid gap-4 sm:grid-cols-2">
                <FF label="Service">
                  <Select value={f.service_category} onValueChange={(v) => setF({ ...f, service_category: v })}>
                    <SelectTrigger data-testid="cap-service-sel"><SelectValue /></SelectTrigger>
                    <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </FF>
                <FF label="Provider type"><Input data-testid="cap-type" value={f.provider_type} onChange={set("provider_type")} /></FF>
                <FF label="City" req><Input data-testid="cap-city-in" required value={f.city} onChange={set("city")} /></FF>
                <FF label="State" req><Input data-testid="cap-state-in" required value={f.state} onChange={set("state")} /></FF>
                <FF label="Quantity"><Input data-testid="cap-qty" value={f.quantity} onChange={set("quantity")} placeholder="e.g. 2 beds" /></FF>
                <FF label="Price range"><Input data-testid="cap-price" value={f.price_range} onChange={set("price_range")} /></FF>
              </div>
              <FF label="Description" req><Input data-testid="cap-desc" required value={f.description} onChange={set("description")} placeholder="e.g. 2 assisted-living beds available" /></FF>
              <Button type="submit" data-testid="cap-submit-btn" className="w-full bg-navy hover:bg-navy/90">Add capacity</Button>
            </form>
          </DialogContent>
        </Dialog>
      </PageTitle>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((c) => (
          <Card key={c.id} className="p-5" data-testid={`capacity-item-${c.id}`}>
            <div className="flex items-center justify-between">
              <Badge className="bg-sky text-clinical hover:bg-sky">{c.service_category}</Badge>
              <button onClick={() => del(c.id)} data-testid={`cap-del-${c.id}`}><Trash2 className="h-4 w-4 text-slate-400 hover:text-destructive" /></button>
            </div>
            <p className="mt-3 font-heading font-semibold text-navy">{c.description}</p>
            <p className="mt-1 text-sm text-slate-500">{c.provider_name} · {c.city}, {c.state}</p>
            <div className="mt-3 flex justify-between text-sm"><span className="text-slate-400">{c.quantity}</span><span className="font-semibold text-emeraldbrand">{c.price_range}</span></div>
          </Card>
        ))}
        {items.length === 0 && <Card className="col-span-full p-10 text-center text-slate-400">No capacity yet.</Card>}
      </div>
    </div>
  );
}
function FF({ label, req, children }) {
  return <div><Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}{req && " *"}</Label><div className="mt-1">{children}</div></div>;
}
